import React, { useCallback, useEffect, useMemo } from 'react';
import { useChangelogTFunction } from '@retail/changelog/i18n';
import { useConsumerType, useSanityLanguageCode } from '@retail/sanity/hooks';
import { useInfiniteChangelogEntries } from '@retail/sanity/data-access';
import dayjs from 'dayjs';
import { ChangelogEntry, ChangelogEntryDTO } from '@retail/changelog/types';
import { ChangelogEntriesMenuList } from '@retail/changelog/components';
import { MenuItem, Typography } from '@mui/material';
import { useAlertedChangelogEntries } from './useAlertedChangelogEntries';
import { useSelectedOrgUnit } from '@retail/app/stores/selected-context';

interface Props {
  isOpen: boolean;
}

export const ChangelogEntriesMenuContent: React.FC<Props> = ({ isOpen }) => {
  const t = useChangelogTFunction();
  const language = useSanityLanguageCode();
  const consumer = useConsumerType();
  const { hasValue, addValue } = useAlertedChangelogEntries();
  const { appTenant } = useSelectedOrgUnit();

  const { data, hasNextPage, isFetchingNextPage, fetchNextPage, status } =
    useInfiniteChangelogEntries(
      {
        language,
        consumer,
        country: appTenant.countryCode,
        pageSize: 3,
      },
      { enabled: isOpen }
    );

  const today = dayjs();

  const getHasSeen = useCallback(
    (entry: ChangelogEntryDTO) => {
      if (hasValue(entry.id)) return true;
      return dayjs(entry.changeDate).isBefore(today.subtract(2, 'months'));
    },
    [hasValue, today]
  );

  const entries = useMemo(
    () =>
      data?.pages?.flatMap((page) =>
        Array.from(page.values()).map((entry) => ChangelogEntry.fromDto(entry, getHasSeen(entry)))
      ) ?? [],
    [data?.pages, getHasSeen]
  );

  const setHasSeenAllUnseenEntries = useCallback(() => {
    entries
      .filter(({ seen }) => !seen)
      .forEach((entry) => {
        addValue(entry.id);
      });
  }, [addValue, entries]);

  useEffect(() => {
    if (!isOpen) {
      setHasSeenAllUnseenEntries();
    }
  }, [isOpen, setHasSeenAllUnseenEntries]);

  if (status === 'loading' && entries.length === 0) {
    return (
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      <ChangelogEntriesMenuList entries={[]} hasNextPage loadMore={() => {}} isFetchingNextPage />
    );
  }

  if (status === 'error') {
    return (
      <MenuItem disabled>
        <Typography color="error">{t('changelog.error.loadingChangelogEntries')}</Typography>
      </MenuItem>
    );
  }

  return (
    <ChangelogEntriesMenuList
      entries={entries}
      hasNextPage={!!hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      loadMore={fetchNextPage}
    />
  );
};
